import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, {keyframes} from "styled-components";
import api from "../../../api/api";
import {
  Button,
  Col,
  DatePicker,
  Input,
  message,
  Row,
  Space,
  Table,
  Select,
  Empty,
  Popover,
} from "antd";
import {
  IconRight,
  IconSearch,
  IconCheck,
  IconReject,
  IconLeft,
} from "../../../assets/icons";
import { ORDER_STATUS } from "../../../constants";
import { getFormattedNumber } from "@utils";
import moment from "moment";
import { PRIVATE_ROUTE } from "../../../route.constants";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CheckModal from "../OrderDetails/CheckModal/CheckModal";
import PickupModal from "../OrderDetails/PickupModal/PickupModal";
import RejectModal from "../OrderDetails/RejectModal/RejectModal";
import _ from "lodash";
import dayjs from "dayjs";
import {
  GrayTitle,
  TitleWrapper,
  Topbar,
} from "../../Dashboard/BestSellingProducts";
import { SubTitle } from "../../DataManagement/UserManagement/UserManagement";
import { useAtom } from "jotai";
import { orderCount, savedOrderTablePage } from "../../../utils/atom";
import { useTranslation } from "react-i18next";
import { IconQRCode } from "../../../assets/icons";
import { CameraOutlined, CheckCircleFilled, CloseOutlined, CloudUploadOutlined } from "@ant-design/icons";

const RootContainer = styled.div`
  .ant-radio-group {
    margin-bottom: 24px !important;
    display: flex;
    gap: 16px;
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .ant-radio-button-wrapper {
    width: 25%;
    padding: 8px 12px;
    height: 48px;
    border-radius: 8px;
    border-inline-start-width: 1px;
    border: 1px solid ${(props) => props.theme.primaryColor};
    background-color: ${(props) => props.theme.backgroundColor};
    color: #1d1014;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    white-space: nowrap;

    ::before {
      display: none;
    }
  }

  .ant-radio-button-wrapper-checked {
    background-color: ${(props) => props.theme.primaryColor};
    color: #fff;

    :hover {
      color: #fff;
    }
  }
`;

const Title = styled.div`
  padding-top: 16px;
  padding-bottom: 24px;
  font-size: 24px;
  font-weight: 700;
  color: #1e1e1e;
`;

const Top = styled(Row)`
  margin-bottom: 24px;

  .ant-input {
    flex: 1;
  }

  .ant-picker {
    width: 100%;
    height: 48px;
  }

  .ant-select {
    width: 100%;
  }
`;

const ActionsWrapper = styled(Space)`
  .ant-btn-default {
    width: 172px;
    height: 32px;
    background: ${(props) => props.theme.backgroundColor};
    color: ${(props) => props.theme.primaryColor} !important;
    border: none;
    font-size: 14px;
    font-weight: 600;

    :hover {
      background: ${(props) => props.theme.backgroundColor} !important;
    }
  }

  .ant-btn-dangerous {
    background: #ffe6e8 !important;
    color: #f00 !important;

    :hover {
      background: #ffe6e8 !important;
    }
  }

  svg {
    cursor: pointer;
  }
`;

/* ------------------------------------------------------------------ */
/*  QR choice popover                                                  */
/* ------------------------------------------------------------------ */
const QRWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const QRIconButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(props) => (props.$disabled ? "#b9b9b9" : "#1859ff")};
  transition: transform 0.15s ease;

  &:hover {
    transform: ${(props) => (props.$disabled ? "none" : "scale(1.08)")};
  }
`;

const MenuWrap = styled.div`
  width: 260px;
  padding: 6px;
`;

const MenuTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #9aa1b2;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 8px 10px 10px;
`;

const MenuOption = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #eef3ff;
  }
`;

const MenuIconWrap = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1859ff 0%, #1450e0 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const MenuOptionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1a1d29;
`;

const MenuOptionSubtitle = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 1px;
`;

/* ------------------------------------------------------------------ */
/*  Premium loading overlay (image upload / processing)                */
/* ------------------------------------------------------------------ */
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  z-index: 5;
  animation: fadeIn 0.15s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const SpinnerRing = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2.5px solid #dbe7ff;
  border-top-color: #1859ff;
  animation: ${spin} 0.7s linear infinite;
`;

const LoadingLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1859ff;
`;

const SearchColWrap = styled.div`
  position: relative;
`;

/* ------------------------------------------------------------------ */
/*  Device-scan overlay (waiting for hardware scanner)                 */
/* ------------------------------------------------------------------ */
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(24, 89, 255, 0.35); }
  70% { box-shadow: 0 0 0 22px rgba(24, 89, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(24, 89, 255, 0); }
`;

const ScanOverlayBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(20, 22, 30, 0.55);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ScanCard = styled.div`
  width: 360px;
  max-width: 90vw;
  background: #fff;
  border-radius: 20px;
  padding: 32px 28px 26px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
`;

const ScanCloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  border: none;
  background: transparent;
  color: #9aa1b2;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #4b5468;
  }
`;

const ScanPulseWrap = styled.div`
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: #eef3ff;
  color: #1859ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  margin-bottom: 20px;
  animation: ${pulse} 1.6s infinite;
`;

const ScanTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #1a1d29;
  margin-bottom: 6px;
`;

const ScanSubtitle = styled.div`
  font-size: 13px;
  color: #8c8c8c;
  line-height: 19px;
  margin-bottom: 20px;
`;

const HiddenScanInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  pointer-events: none;
`;

const ScanSuccessWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2e9e44;
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 4px;
`;

const { PENDING, UNPAID, CONFIRMED, COMPLETED, CANCELLED } = ORDER_STATUS;
const { HOME, ORDER_MGNT, ORDERS } = PRIVATE_ROUTE;

const Orders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { status } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totalCount, seTotalCount] = useState();
  const [counts, setCounts] = useState({
    pendingCount: 0,
    unpaidCount: 0,
    confirmedCount: 0,
    completedCount: 0,
    cancelledCount: 0,
    totalOrderCount:0,
  });
  const [selectedItem, setSelectedItem] = useState({});
  const [openCheck, setOpenCheck] = useState(false);
  const [openPickup, setOpenPickup] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [keyword, setKeyword] = useState("");
 const [selectValue, setSelectValue] = useState(null);
const [dates, setDates] = useState(null);
  const [sortPageSize, setSortPageSize] = useState(10);
  const [savedOrderPage,setSavedOrderPage] = useAtom(savedOrderTablePage);
  const [sortPage, setSortPage] = useState(savedOrderPage ?? 1);
    const [orderStatus,] = useAtom(orderCount);
  
  const [searchValue, setSearchValue] = useState("");

  const {t} = useTranslation();
  const qrFileInputRef = useRef(null);
  const deviceScanInputRef = useRef(null);
  const scanBufferRef = useRef("");

  const [qrScanning, setQrScanning] = useState(false); // image-upload processing
  const [checkQR, setCheckQR] = useState(false);
  const [qrMenuOpen, setQrMenuOpen] = useState(false);
  const [deviceScanOpen, setDeviceScanOpen] = useState(false);
  const [deviceScanResult, setDeviceScanResult] = useState(null); // brief success flash

  const SORT_MAP = {
  [PENDING]: "accepted_at:desc",
  [UNPAID]:   "created_at:desc",
  [CONFIRMED]: "confirmed_at:desc",
  [COMPLETED]: "completed_at:desc",
  [CANCELLED]: "cancelled_at:desc",
};
  

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  }, []);

  const [storeCurrency,setStoreCurrency] = useState();
  
   const getPanelInfo = () => {
      api
        .getPanelInfo()
        .then(({ data }) => {
          setStoreCurrency(data);
        })
        .catch((error) => {
          message.error(error);
        });
    };
  
      const checkQRScan = () => {
      api
        .checkQRScan()
        .then(({ data }) => {
          setCheckQR(data?.status === "ACTIVE" ? true : false);
        })
        .catch((error) => {
          message.error(error);
        });
    };
  
    useEffect(() => {
        getPanelInfo();
        checkQRScan();
    }, []);
  
useEffect(() => {
  if (!status || selectValue === null) return;

  getOrders(status, keyword, dates, sortPage, sortPageSize);
}, [status, keyword, dates, selectValue, sortPage, sortPageSize,orderStatus]);

// const getOrders = (status, keyword, dates, page = sortPage, pageSize = sortPageSize) => {
//   setLoading(true);
//   setData([]);

//   let date_from = null;
//   let date_to = null;

//   if (selectValue === "today") {
//     date_from = dayjs().startOf("day").format("YYYY-MM-DD");
//     date_to = dayjs().endOf("day").format("YYYY-MM-DD");
//   }

//   if (
//     selectValue === "custom" &&
//     Array.isArray(dates) &&
//     dates?.[0] &&
//     dates?.[1]
//   ) {
//     date_from = dayjs(dates[0]).startOf("day").format("YYYY-MM-DD");
//     date_to = dayjs(dates[1]).endOf("day").format("YYYY-MM-DD");
//   }

//   const sort = SORT_MAP[status] || "created_at:desc";

//   let hasDeliveryRequest;
//   if ([PENDING, UNPAID, CONFIRMED].includes(status)) {
//     hasDeliveryRequest = false;
//   }

//   api
//     .getOrders({
//       status,
//       keyword,
//       sort,
//       has_delivery_request: hasDeliveryRequest,
//       date_from,
//       date_to,
//       page,
//       page_size: pageSize,
//     })
//     .then(({ data, counts, total }) => {
//       setLoading(false);
//       setData(data);
//       setCounts(counts);
//       seTotalCount(total);
//     })
//     .catch((error) => {
//       setLoading(false);
//       message.error(error);
//     });
// };

const getOrders = (status, keyword, dates, page = sortPage, pageSize = sortPageSize) => {
    setLoading(true);
    setData([]);

    let date_from = null;
    let date_to = null;

    if (selectValue === "today") {
      date_from = dayjs().startOf("day").format("YYYY-MM-DD");
      date_to = dayjs().endOf("day").format("YYYY-MM-DD");
    }

    if (
      selectValue === "custom" &&
      Array.isArray(dates) &&
      dates?.[0] &&
      dates?.[1]
    ) {
      date_from = dayjs(dates[0]).startOf("day").format("YYYY-MM-DD");
      date_to = dayjs(dates[1]).endOf("day").format("YYYY-MM-DD");
    }

    const sort = SORT_MAP[status] || "created_at:desc";

    let hasDeliveryRequest;
    if ([PENDING, UNPAID, CONFIRMED].includes(status)) {
      hasDeliveryRequest = false;
    }

    api
      .getOrders({
        status,
        keyword,
        sort,
        has_delivery_request: hasDeliveryRequest,
        date_from,
        date_to,
        page,
        page_size: pageSize,
      })
      .then(({ data, counts, total }) => {
        setLoading(false);
        setData(data);
        setCounts(counts);
        seTotalCount(total);
      })
      .catch((error) => {
        setLoading(false);
        message.error(error);
      });
  };

  const handleStatusChange = (e) => {
    navigate(`/${HOME}/${ORDER_MGNT}/${ORDERS}/${e.target.value}`, {
      replace: true,
    });
    setSortPage(1);
    setSavedOrderPage(1);
  };

const handleSelectChange = (value) => {
  setSelectValue(value);

  if (value === "all") {
    setDates(null);
  }

  if (value === "today") {
    setDates([dayjs(), dayjs()]);
  }

  if (value === "custom") {
    setDates(null);
  }

  setSortPage(1);
};

useEffect(() => {
  if (!status) return;

  if ((status === PENDING || status === UNPAID)) {
    setSelectValue("all");
    setDates(null);
  } else {
    setSelectValue("today");
    setDates([dayjs(), dayjs()]);
  }
}, [status]);

  const handleDateChange = (dates) => {
    setDates(dates);
  };

  const debouncedResults = useMemo(() => {
    return _.debounce((e) => {
      setKeyword(e.target.value.trim());
    }, 1000);
  }, []);

  const goToDetails = (id) => {
    navigate(`${PRIVATE_ROUTE.ORDER_DETAILS}/${id}`);
    setSavedOrderPage(sortPage);
  };

  const handleCheckClicked = (item) => {
    setSelectedItem(item);
    setOpenCheck(true);
  };

  const handlePickupClicked = (item) => {
    setSelectedItem(item);
    setOpenPickup(true);
  };

  const handleRejectClicked = (item) => {
    setSelectedItem(item);
    setOpenRejectModal(true);
  };

  const handleClose = () => {
    setOpenCheck(false);
    setOpenPickup(false);
    setSelectedItem({});
  };

  const handleRejectClose = () => {
    setOpenRejectModal(false);
  };

  const successCallback = () => {
    getOrders(status);
  };

    const handleSearchInputChange = (e) => {
    setSearchValue(e.target.value);
    debouncedResults(e);
  };

  const status_options = [
    {
      key: PENDING,
      label: `${t('menu_36')}`,
    },
    {
      key: UNPAID,
      label: `${t('menu_37')}`,
    },
    {
      key: CONFIRMED,
      label: `${t('menu_38')}`,
    },
    {
      key: COMPLETED,
      label: `${t('menu_39')}`,
    },
    {
      key: CANCELLED,
      label: `${t('menu_40')}`,
    },
  ];

  const Actions = ({ item }) => {
    switch (status) {
      case PENDING:
        return (
          <ActionsWrapper>
            <Button
              type="default"
              icon={<IconCheck />}
              onClick={() => handleCheckClicked(item)}
            >
              Check
            </Button>
          </ActionsWrapper>
        );
      case UNPAID:
        return (
          <ActionsWrapper>
            <Button
              type="default"
              icon={<IconCheck />}
              onClick={() => handleCheckClicked(item)}
            >
              Confirm
            </Button>
            <Button
              danger
              icon={<IconReject />}
              onClick={() => handleRejectClicked(item)}
            >
              Reject
            </Button>
          </ActionsWrapper>
        );
      case CONFIRMED:
        return (
          <ActionsWrapper>
            <Button
              type="default"
              icon={<IconCheck />}
              onClick={() => handlePickupClicked(item)}
            >
              Pickup
            </Button>
          </ActionsWrapper>
        );
      default:
        return null;
    }
  };

  const columns = [
    {
      key: "no",
      dataIndex: "no",
      title: t('no.'),
      render: (_, record, index) => index + 1,
    },
    {
    key: "time",
    dataIndex: "time",
    title: t('time'),
    render: (_, record) => {
      let timeValue;
      switch (status) {
        case PENDING:
          timeValue = record.accepted_at;
          break;
        case UNPAID:
          timeValue = record.created_at;
          break;
        case CONFIRMED:
          timeValue = record.confirmed_at;
          break;
        case COMPLETED:
          timeValue = record.completed_at;
          break;
        case CANCELLED:
          timeValue = record.cancelled_at || record.created_at;
          break;
        default:
          timeValue = record.created_at;
      }

      return timeValue
        ? moment(timeValue).format("DD MMM YYYY, HH:mm A")
        : "-";
    },
  },
    {
      key: "order_id",
      dataIndex: "order_id",
      title: t('order_id'),
    },
    {
      key: "user_name",
      dataIndex: "user_name",
      title: t('customer_id'),
      render: (_, record) => (
        <>
          {record.user?.name}
          <br />
          {record.user?.username}
        </>
      ),
    },
    {
      key: "total_quantity",
      dataIndex: "total_quantity",
      title: t('quantity&amount'),
      render: (_, record) =>
        `${getFormattedNumber(
          status === "CANCELLED"
            ? record.product_count
            : record.product_count - record.cancel_count
        )} / ${getFormattedNumber(record.final_amount)}`,
    },
    {
      key: "actions",
      title: t('actions'),
      render: (_, record) => (
        <ActionsWrapper>
          {/* <Actions item={record} /> */}
          <Button
            style={{ backgroundColor: "#F0F6FE", color: "#005DFF" }}
            onClick={() => goToDetails(record.id)}
            type="text"
          >
            {t('view_details')}
          </Button>
          <IconRight onClick={() => goToDetails(record.id)} />
        </ActionsWrapper>
      ),
    },
  ];

  const goBack = () => {
    navigate(-1);
  };

  useEffect(() => {
  setSortPage(1);
}, [status, keyword, selectValue, dates]);


  /* ------------------------------------------------------------------ */
  /*  QR: shared "found order" handling — populate search, never auto-nav */
  /* ------------------------------------------------------------------ */
  const applyFoundOrder = (orderIdValue) => {
    setSearchValue(orderIdValue);
    setKeyword(orderIdValue);
    setSortPage(1);
    message.success(t("qr_order_found"));
  };

  /* ------------------------------------------------------------------ */
  /*  Option 1: Upload QR image                                          */
  /* ------------------------------------------------------------------ */
  const handleUploadOptionClick = () => {
    setQrMenuOpen(false);
    qrFileInputRef.current?.click();
  };

  const handleQRFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    setQrScanning(true);

    api
      .findByQRScan(formData)
      .then(({ data }) => {
        setQrScanning(false);

        const orderIdValue = data?.order_id;
        if (orderIdValue) {
          applyFoundOrder(orderIdValue);
        } else {
          message.error(t("qr_order_not_found"));
        }
      })
      .catch((error) => {
        setQrScanning(false);
        message.error(error || t("qr_scan_failed"));
      });
  };

  /* ------------------------------------------------------------------ */
  /*  Option 2: External scanner device                                  */
  /*  NOTE: hardware QR/barcode scanners decode locally and emulate a     */
  /*  keyboard, "typing" the decoded text + Enter into whatever input is  */
  /*  focused. There is no upload/API step here — the decoded text is     */
  /*  used directly as the order id to search by.                        */
  /* ------------------------------------------------------------------ */
  const handleDeviceOptionClick = () => {
    setQrMenuOpen(false);
    setDeviceScanResult(null);
    scanBufferRef.current = "";
    setDeviceScanOpen(true);
  };

  useEffect(() => {
    if (deviceScanOpen && deviceScanInputRef.current) {
      // focus on the next tick so the overlay is mounted first
      const t = setTimeout(() => deviceScanInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [deviceScanOpen]);

  const handleDeviceScanKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      finalizeDeviceScan();
    }
  };

  const handleDeviceScanChange = (e) => {
    scanBufferRef.current = e.target.value;
  };

  const finalizeDeviceScan = () => {
    const scanned = scanBufferRef.current.trim();
    scanBufferRef.current = "";
    if (deviceScanInputRef.current) deviceScanInputRef.current.value = "";

    if (!scanned) {
      message.warning(t("qr_scan_no_input"));
      return;
    }

    setDeviceScanResult(scanned);

    // brief success flash before closing, matches the "premium" feel
    setTimeout(() => {
      setDeviceScanOpen(false);
      setDeviceScanResult(null);
      applyFoundOrder(scanned);
    }, 650);
  };

  const closeDeviceScan = () => {
    setDeviceScanOpen(false);
    setDeviceScanResult(null);
    scanBufferRef.current = "";
  };

  const qrMenuContent = (
    <MenuWrap>
      <MenuTitle>{t("scan_order_qr")}</MenuTitle>
      <MenuOption onClick={handleUploadOptionClick}>
        <MenuIconWrap>
          <CloudUploadOutlined />
        </MenuIconWrap>
        <div>
          <MenuOptionTitle>{t("upload_qr_image")}</MenuOptionTitle>
          <MenuOptionSubtitle>{t("upload_qr_image_desc")}</MenuOptionSubtitle>
        </div>
      </MenuOption>
      <MenuOption onClick={handleDeviceOptionClick}>
        <MenuIconWrap>
          <CameraOutlined />
        </MenuIconWrap>
        <div>
          <MenuOptionTitle>{t("scan_with_device")}</MenuOptionTitle>
          <MenuOptionSubtitle>{t("scan_with_device_desc")}</MenuOptionSubtitle>
        </div>
      </MenuOption>
    </MenuWrap>
  );

  return (
    <RootContainer>
      {location.state === "dashboard" && (
        <Topbar>
          <Button type="default" icon={<IconLeft />} onClick={goBack}>
            {t('back')}
          </Button>
          <TitleWrapper>
            <GrayTitle>{t('dashboard')}</GrayTitle>
            <IconRight />
            <SubTitle>{t('total_orders')}</SubTitle>
          </TitleWrapper>
        </Topbar>
      )}
      {/* <Title>{t('total_orders')} ({counts?.totalOrderCount})</Title> */}
       {status_options
          .filter((s) => s.key === status)
          .map((s) => (
            <Title
              key={s.key}
              value={s.key} 
            >
              {s.label}
            </Title>
          ))}
      <Top gutter={[16, 16]}>
         <Col xs={24} md={8}>
          <SearchColWrap>
            <Input
              placeholder={t("search_orderId_name")}
              prefix={<IconSearch />}
              value={searchValue}
              suffix={
                checkQR ? (
                  <QRWrap>
                    <Popover
                      content={qrMenuContent}
                      trigger="click"
                      open={qrMenuOpen}
                      onOpenChange={setQrMenuOpen}
                      placement="bottomRight"
                      overlayInnerStyle={{ borderRadius: 14, padding: 4 }}
                    >
                      <QRIconButton $disabled={qrScanning}>
                        <IconQRCode />
                      </QRIconButton>
                    </Popover>
                  </QRWrap>
                ) : (
                  ""
                )
              }
              onChange={handleSearchInputChange}
              allowClear
              onClear={() => {
                setSearchValue("");
                setKeyword("");
              }}
            />
            {qrScanning && (
              <LoadingOverlay>
                <SpinnerRing />
                <LoadingLabel>{t("scanning_qr")}</LoadingLabel>
              </LoadingOverlay>
            )}
          </SearchColWrap>
          {checkQR && (
            <input
              ref={qrFileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              hidden
              onChange={handleQRFileChange}
            />
          )}
        </Col>
        {selectValue !== "custom" && <Col xs={24} md={8} />}
        <Col xs={24} md={8} style={{ display: "flex", justifyContent: "end" }}>
       <Select
          value={selectValue}
          onChange={handleSelectChange}
          style={{ width: "100%", height: 48 }}
          options={[
          { value: "all", label: t("all_time") },
          {
            value: "today",
            label: dayjs().format("DD MMM YYYY"),
          },
          { value: "custom", label: t("custom_date") },
        ]}
        />
        </Col>
        {selectValue === "custom" && (
          <Col xs={24} md={8}>
          <DatePicker.RangePicker
            format="YYYY-MM-DD"
            value={dates}
            onChange={handleDateChange}
            style={{ width: "100%", height: 48 }}
          />
          </Col>
        )}
      </Top>
     <Table
        dataSource={data}
        columns={[
          {
            key: "no",
            dataIndex: "no",
            title: t("no."),
            render: (_, __, index) => (sortPage - 1) * sortPageSize + index + 1,
          },
          ...columns.slice(1),
        ]}
        loading={loading}
        locale={{
        emptyText: (
          <Empty
            description={t("no_data")}
          />
        ),
      }}
        pagination={{
          current: sortPage,
          pageSize: sortPageSize,
          total: totalCount,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: (page, pageSize) => {
            setSortPage(page);
            setSortPageSize(pageSize);
          },
        }}
        scroll={{ x: 1000 }}
        rowKey="id"
      />
    {deviceScanOpen && (
        <ScanOverlayBackdrop onClick={(e) => e.target === e.currentTarget && closeDeviceScan()}>
          <ScanCard>
            <ScanCloseBtn onClick={closeDeviceScan}>
              <CloseOutlined style={{ fontSize: 18 }} />
            </ScanCloseBtn>

            {deviceScanResult ? (
              <>
                <ScanPulseWrap style={{ animation: "none", background: "#e9f9ec", color: "#2e9e44" }}>
                  <CheckCircleFilled />
                </ScanPulseWrap>
                <ScanSuccessWrap>{t("qr_scan_captured")}</ScanSuccessWrap>
                <ScanSubtitle>{deviceScanResult}</ScanSubtitle>
              </>
            ) : (
              <>
                <ScanPulseWrap>
                  <CameraOutlined />
                </ScanPulseWrap>
                <ScanTitle>{t("waiting_for_scan")}</ScanTitle>
                <ScanSubtitle>{t("waiting_for_scan_desc")}</ScanSubtitle>
                <Button onClick={closeDeviceScan}>{t("cancel")}</Button>
              </>
            )}

            <HiddenScanInput
              ref={deviceScanInputRef}
              type="text"
              autoFocus
              onKeyDown={handleDeviceScanKeyDown}
              onChange={handleDeviceScanChange}
              onBlur={() => {
                if (deviceScanOpen && !deviceScanResult) {
                  setTimeout(() => deviceScanInputRef.current?.focus(), 10);
                }
              }}
            />
          </ScanCard>
        </ScanOverlayBackdrop>
      )}
      <CheckModal
        open={openCheck}
        id={selectedItem.id}
        total_price={selectedItem?.total_price}
        products={selectedItem?.OrderProducts}
        setData={setSelectedItem}
        handleClose={handleClose}
        successCallback={successCallback}
      />

      <PickupModal
        open={openPickup}
        id={selectedItem.id}
        final_amount={selectedItem?.final_amount}
        products={selectedItem?.OrderProducts}
        handleClose={handleClose}
        successCallback={successCallback}
      />

      <RejectModal
        id={selectedItem.id}
        open={openRejectModal}
        handleClose={handleRejectClose}
        successCallback={successCallback}
      />
    </RootContainer>
  );
};

export default Orders;
